import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { configureAuth, configureUnauthorizedHandler } from '../../lib/api';
import type { AdminPanelRole } from './permissions';

// Admin-Session-State. Persistierung in localStorage, damit ein Page-Reload
// den User nicht ausloggt. Refresh-Token bleibt im Speicher (nicht in
// localStorage), weil HttpOnly-Cookies in Phase 2 nachgezogen werden.

export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  // 'ADMIN_VIEWER' ist die Nur-Lesen-Rolle (CUR-995). Was sie sehen und tun
  // darf, steht in `permissions.ts` — nicht hier.
  role: AdminPanelRole;
}

interface AuthState {
  user: AdminUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  // Transienter Marker: wurde die Session gerade durch einen 401 aus der API
  // beendet? LoginPage liest das, um statt des Standard-Login-Hinweises ein
  // "Sitzung abgelaufen"-Banner zu zeigen. Nicht persistiert (siehe partialize).
  sessionExpired: boolean;

  setSession: (params: { user: AdminUser; accessToken: string; refreshToken: string }) => void;
  setAccessToken: (token: string, refreshToken: string) => void;
  clearSession: () => void;
  markSessionExpired: () => void;
  acknowledgeSessionExpired: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      sessionExpired: false,

      setSession: ({ user, accessToken, refreshToken }) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          sessionExpired: false,
        }),

      setAccessToken: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      clearSession: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          sessionExpired: false,
        }),

      // Wird aus dem zentralen 401-Handler getriggert. Nur als "expired"
      // markieren, wenn vorher tatsaechlich eine Session existierte — sonst
      // wuerde das Banner auch bei 401 vom Login-Endpoint selbst aufploppen
      // (z.B. "Account not found").
      markSessionExpired: () => {
        if (!get().accessToken) return;
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          sessionExpired: true,
        });
      },

      acknowledgeSessionExpired: () => set({ sessionExpired: false }),
    }),
    {
      name: 'currivium-admin-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

// API-Client mit aktuellem Token versorgen. Wird einmalig beim App-Start
// aufgerufen (siehe main.tsx) — Token wird live aus dem Store gelesen.
// Bei 401 ruft der API-Wrapper markSessionExpired() — ProtectedRoute leitet
// dann automatisch nach /login um, weil isAuthenticated=false wird.
export function bindAuthToApiClient(): void {
  configureAuth(() => useAuthStore.getState().accessToken);
  configureUnauthorizedHandler(() => {
    useAuthStore.getState().markSessionExpired();
  });
}
